import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Save, X, Send, Image as ImageIcon } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { uploadImage } from "../../lib/uploads";

const empty = {
  name: "", brand: "", category: "luxury", price: 0, original_price: 0,
  description: "", images: [], stock: 1, sku: "", gender: "unisex",
  movement: "", case_size: "", case_material: "", strap: "", water_resistance: "",
  featured: false, is_new: false, sections: [], low_stock_threshold: 3,
};

const SECTIONS = [
  { id: "vintage_luxury", label: "Vintage & Luxury Watches" },
  { id: "sport_chronograph", label: "Sport & Chronograph" },
  { id: "new_arrivals", label: "New Arrivals" },
  { id: "on_sale", label: "On Sale" },
];

export default function ShopAddProductManual() {
  const [form, setForm] = useState({ ...empty });
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.images.length + files.length > 5) return toast.error("Max 5 images per product");
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} exceeds 4 MB — skipped`); continue; }
        const r = await uploadImage(f, "products/manual");
        setForm((s) => ({ ...s, images: [...s.images, r.url] }));
      }
      toast.success("Images uploaded");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setUploading(false); }
  };

  const removeImage = (i) => setForm((s) => ({ ...s, images: s.images.filter((_, idx) => idx !== i) }));

  const toggleSection = (id) => setForm((s) => ({
    ...s,
    sections: s.sections.includes(id) ? s.sections.filter((x) => x !== id) : [...s.sections, id],
  }));

  const submit = async () => {
    if (!form.name) return toast.error("Name is required");
    if (!form.price || form.price <= 0) return toast.error("Price must be > 0");
    if (form.images.length < 1) return toast.error("Add at least one image (2-3 recommended)");
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        stock: parseInt(form.stock) || 0,
        low_stock_threshold: parseInt(form.low_stock_threshold) || 3,
      };
      await api.post("/products", payload);
      toast.success("Submitted for admin approval");
      setForm({ ...empty });
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="overline mb-2">Catalog</div>
        <h1 className="font-display text-4xl text-white">Add Product (Manual)</h1>
        <p className="text-sm text-white/55 mt-2">Upload 2-3 product photos and submit for admin approval. Once approved, your product goes live on the storefront.</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
        {/* Images */}
        <div className="card-luxe p-6 space-y-4">
          <div className="overline">1. Product Images</div>
          <div className="grid grid-cols-3 gap-2">
            {form.images.map((src, i) => (
              <div key={i} className="relative aspect-square bg-[#070707] border border-white/10 overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover"/>
                <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500/80 text-white p-1"><X size={11}/></button>
              </div>
            ))}
            {form.images.length < 5 && (
              <label data-testid="manual-upload" className="aspect-square bg-[#070707] border border-dashed border-white/15 hover:border-copper cursor-pointer flex items-center justify-center text-white/40">
                {uploading ? "Uploading…" : <div className="text-center"><Upload size={20} className="mx-auto mb-1"/><div className="text-[10px] uppercase tracking-luxe">Add</div></div>}
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} disabled={uploading}/>
              </label>
            )}
          </div>
          <div className="text-[10px] text-white/40">Max 5 images. JPG / PNG / WEBP up to 4 MB each. Will be uploaded to S3 if configured, otherwise stored as base64.</div>
        </div>

        {/* Details */}
        <div className="card-luxe p-6 space-y-4">
          <div className="overline">2. Product Details</div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="m-name" placeholder="Product name *" className="input-box col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/>
            <input data-testid="m-brand" type="text" placeholder="Brand (e.g. Rolex) *" className="input-box" value={form.brand} list="shop-brand-suggestions" onChange={(e) => setForm({ ...form, brand: e.target.value })}/><datalist id="shop-brand-suggestions">{brands.map((b) => <option key={b.id} value={b.name}/>)}</datalist>
            <select className="input-box" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
            <input data-testid="m-price" type="number" placeholder="Price (AED) *" className="input-box" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}/>
            <input data-testid="m-original" type="number" placeholder="Original price (AED) — optional" className="input-box" value={form.original_price || ""} onChange={(e) => setForm({ ...form, original_price: e.target.value })}/>
            <input type="number" placeholder="Stock" className="input-box" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })}/>
            <input placeholder="SKU" className="input-box" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}/>
            <select className="input-box" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}>
              <option value="unisex">Unisex</option><option value="men">Men</option><option value="women">Women</option>
            </select>
            <input placeholder="Movement" className="input-box" value={form.movement} onChange={(e) => setForm({ ...form, movement: e.target.value })}/>
            <input placeholder="Case size (e.g. 41mm)" className="input-box" value={form.case_size} onChange={(e) => setForm({ ...form, case_size: e.target.value })}/>
            <input placeholder="Case material" className="input-box" value={form.case_material} onChange={(e) => setForm({ ...form, case_material: e.target.value })}/>
            <input placeholder="Strap" className="input-box" value={form.strap} onChange={(e) => setForm({ ...form, strap: e.target.value })}/>
            <textarea rows={3} placeholder="Description" className="input-box col-span-2 resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
          </div>

          {/* Sections */}
          <div>
            <div className="overline mb-2">3. Storefront Sections</div>
            <div className="text-[11px] text-white/55 mb-2">Pick where this product should appear on the home page. You can pick multiple — e.g. a watch can be both <em className="text-copper not-italic">Sport & Chronograph</em> AND <em className="text-copper not-italic">On Sale</em>.</div>
            <div className="flex flex-wrap gap-2">
              {SECTIONS.map((sec) => {
                const selected = form.sections.includes(sec.id);
                return (
                  <button
                    key={sec.id}
                    type="button"
                    data-testid={`section-${sec.id}`}
                    onClick={() => toggleSection(sec.id)}
                    className="px-4 py-2 text-xs uppercase tracking-luxe border transition-colors"
                    style={selected
                      ? { backgroundColor: "#B89766", color: "#fff", borderColor: "#B89766" }
                      : { backgroundColor: "transparent", color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.15)" }}
                  >
                    {sec.label}
                  </button>
                );
              })}
            </div>
            <label className="flex items-center gap-2 text-xs text-white/70 mt-3">
              <input type="checkbox" checked={form.is_new} onChange={(e) => setForm({ ...form, is_new: e.target.checked })}/> Tag as “New” (auto-shown in New Arrivals)
            </label>
          </div>

          <button data-testid="m-submit" disabled={saving} onClick={submit} className="btn-primary w-full justify-center">
            <Send size={14}/> {saving ? "Submitting…" : "Submit for Admin Approval"}
          </button>
        </div>
      </div>
    </div>
  );
}
